import { useState, useEffect, useCallback } from "react";

interface UseLocalStorageOptions {
    prefix?: string;
    serialize?: (value: unknown) => string;
    deserialize?: (value: string) => unknown;
}

const useLocalStorage = <T>(
    key: string,
    initialValue?: T,
    options: UseLocalStorageOptions = {},
) => {
    const {
        prefix = "treez_",
        serialize = JSON.stringify,
        deserialize = JSON.parse,
    } = options;

    const prefixedKey = `${prefix}${key}`;

    // Get value from localStorage or return initial value
    const getStoredValue = useCallback((): T => {
        try {
            const item = window.localStorage.getItem(prefixedKey);
            return item ? (deserialize(item) as T) : initialValue;
        } catch (error) {
            console.warn(
                `Error reading localStorage key "${prefixedKey}":`,
                error,
            );
            return initialValue;
        }
    }, [prefixedKey, initialValue, deserialize]);

    // State to store our value
    const [storedValue, setStoredValue] = useState<T>(getStoredValue);

    // Return a wrapped version of useState's setter function that persists the new value to localStorage
    const setValue = useCallback(
        (value: T | ((val: T) => T)) => {
            try {
                // Allow value to be a function so we have the same API as useState
                const valueToStore =
                    value instanceof Function ? value(storedValue) : value;

                // Save state
                setStoredValue(valueToStore);

                // Save to localStorage
                window.localStorage.setItem(
                    prefixedKey,
                    serialize(valueToStore),
                );
            } catch (error) {
                console.warn(
                    `Error setting localStorage key "${prefixedKey}":`,
                    error,
                );
            }
        },
        [prefixedKey, storedValue, serialize],
    );

    // Remove value from localStorage
    const removeValue = useCallback(() => {
        try {
            window.localStorage.removeItem(prefixedKey);
            setStoredValue(initialValue);
        } catch (error) {
            console.warn(
                `Error removing localStorage key "${prefixedKey}":`,
                error,
            );
        }
    }, [prefixedKey, initialValue]);

    // Listen for changes to this localStorage key from other tabs/windows
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === prefixedKey && e.newValue !== null) {
                try {
                    setStoredValue(deserialize(e.newValue) as T);
                } catch (error) {
                    console.warn(
                        `Error deserializing localStorage value for key "${prefixedKey}":`,
                        error,
                    );
                }
            }
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, [prefixedKey, deserialize]);

    return [storedValue, setValue, removeValue] as const;
};

export { useLocalStorage };
